import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { PublicLayout } from './components/public/PublicLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppShell } from './components/layout/AppShell';
import { GatedRoute } from './components/routing/GatedRoute';
import { LegacyRedirect } from './components/routing/LegacyRedirect';
import { InspectionStatusRouter } from './components/routing/InspectionStatusRouter';
import { EmptyState, RouteDenied } from './components/ui';
import { useAuth } from './lib/auth';
import { LandingPage } from './pages/LandingPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { PricingPage } from './pages/PricingPage';
import { SecurityPage } from './pages/SecurityPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { ItemsListPage } from './pages/ItemsListPage';
import { ActivityPage } from './pages/ActivityPage';
import { SettingsPage } from './pages/SettingsPage';
import { OrgAccessPage } from './pages/OrgAccessPage';
import { OrgFeaturesPage } from './pages/OrgFeaturesPage';
import { OrgBrandingPage } from './pages/OrgBrandingPage';
import { PlatformConsolePage } from './pages/PlatformConsolePage';
import { TemplateEditorPage } from './pages/TemplateEditorPage';
import { SitesPage } from './pages/SitesPage';
import { SiteDetailPage } from './pages/SiteDetailPage';
import { DesignSystemPage } from './pages/DesignSystemPage';
import { SharedReportPage } from './pages/SharedReportPage';
import { useModuleRoutes } from './lib/host';
import { useCapabilities } from './lib/capabilities';

// Design-system gallery (D44) — gated on its 1F-2 featureKey (story 2-20: the
// flag used to gate nothing). Always mounted at its path (story 2-45): a
// superadmin toggling it off (2-10 org toggle) now renders the in-app
// RouteDenied surface here instead of unmounting the route and falling
// through to the SPA catch-all (which used to bounce mid-session to the
// marketing landing page).
function DesignGalleryRoute(): JSX.Element {
  const { featureState } = useCapabilities();
  if (featureState('design.gallery').state === 'on') return <DesignSystemPage />;
  return <RouteDenied reason="locked" featureKey="design.gallery" name="Design System Gallery" />;
}

// Authenticated hard-load of a path that matches nothing — a genuine 404, not
// a denied route (those now render RouteDenied at their own path — see
// ModuleRoutes.tsx and DesignGalleryRoute above). Still rendered inside the
// AppShell chrome (nav/topbar stay) so it never reads as a logout.
function AuthenticatedNotFound(): JSX.Element {
  const navigate = useNavigate();
  return (
    <AppShell>
      <EmptyState
        title="Page not found"
        description="The page you're looking for doesn't exist or may have moved."
        actionLabel="Back to dashboard"
        onAction={() => navigate('/app')}
        headingLevel={1}
      />
    </AppShell>
  );
}

// The SPA's `*` catch-all (story 2-45). Before this story it always bounced to
// the marketing landing page — indistinguishable from a logout for an
// authenticated user hitting a stale link or typo. Now it only does that for a
// genuinely unauthenticated visitor (unchanged behaviour); an authenticated
// user gets an in-shell 404 instead. Denied KNOWN routes (permission/locked
// feature) never reach this — they render RouteDenied at their own matched
// path (ModuleRoutes.tsx, DesignGalleryRoute).
function CatchAllRoute(): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Loading workspace…
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return <AuthenticatedNotFound />;
}

export default function App(): JSX.Element {
  // Feature modules mount their own routes through the host (P18 §1), gated by
  // the capability document. Billing lives in src/modules/billing/ — the shell
  // no longer imports it directly, so removing the folder still compiles.
  const shellModuleRoutes = useModuleRoutes('shell');
  const fullModuleRoutes = useModuleRoutes('full');

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Route>

      {/* Governed external report sharing (P06 §6.1; story 6-1a). Deliberately
          OUTSIDE `PublicLayout` — that layout wears DefectSnap's own marketing
          header/footer, which is exactly the chrome a shared report must NOT
          carry (the org's own branding is what renders instead, from inside
          the page itself). Also outside `ProtectedRoute`: there is no
          session and none of the module/capability-document machinery below
          applies to an anonymous viewer — see the page's own header comment. */}
      <Route path="/share/:token" element={<SharedReportPage />} />

      <Route element={<ProtectedRoute />}>
        {/* Full-screen editor lives outside the AppShell chrome */}
        <Route path="/app/template-editor" element={<TemplateEditorPage />} />
        <Route path="/app/template-editor/:presetId" element={<TemplateEditorPage />} />
        {/* Full-chrome module routes (host-mounted) */}
        {fullModuleRoutes}
        <Route element={<AppShell />}>
          <Route path="/app" element={<DashboardPage />} />
          {/* Route containment (story 2-104): these routes had no gate but
              hiding their nav row — a hard URL load still mounted the real
              page for a principal without the row's permission/feature. Each
              is now wrapped in the SAME nav/settings registry entry the
              sidebar already resolves (lib/registries), never a second
              declaration of who may see it. */}
          <Route path="/app/items" element={<GatedRoute path="/app/items"><ItemsListPage /></GatedRoute>} />
          {/* Thin status router (story 5-12): the legacy inspection-detail
              page fused capture/AI-analysis/report generation/report viewing
              onto one screen. Every one of those jobs now has a real owner —
              capture, review, reports — so this route reads the inspection's
              lifecycle status and sends the visitor to the surface that owns
              it, `replace`d. See InspectionStatusRouter.tsx. */}
          <Route path="/app/items/:id" element={<GatedRoute path="/app/items/:id"><InspectionStatusRouter /></GatedRoute>} />
          {/* Canonical Sites registry (P03 §0, story 3-1b). */}
          <Route path="/app/sites" element={<GatedRoute path="/app/sites"><SitesPage /></GatedRoute>} />
          <Route path="/app/sites/:id" element={<GatedRoute path="/app/sites/:id"><SiteDetailPage /></GatedRoute>} />
          {/* Compatibility window (P03 §6): the legacy route family redirects,
              carrying filters (location.search) and record identity (:id — the
              same value, since 3-1a preserved ids) across. NOT wrapped in
              GatedRoute: the destination is gated, and gating the redirect too
              would render a denial at a URL the user is only passing through. */}
          <Route path="/app/properties" element={<LegacyRedirect to="/app/sites" />} />
          <Route path="/app/properties/:id" element={<LegacyRedirect to="/app/sites/:id" />} />
          {/* /app/reports + /app/reports/:reportId moved to the reports module
              (story 4-9a) — its manifest owns both routes and the nav row now,
              host-mounted below via `shellModuleRoutes`. */}
          {/* /app/report-presets moved to the templates module (story 4-2a) —
              its manifest owns the route + the nav row now, host-mounted below
              via `shellModuleRoutes` (ModuleRoutes.tsx already renders
              RouteDenied for a locked/permission-denied load, so there is no
              GatedRoute wrapper to carry over). */}
          <Route path="/app/activity" element={<ActivityPage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route
            path="/app/settings/access"
            element={
              <GatedRoute path="/app/settings/access">
                <OrgAccessPage />
              </GatedRoute>
            }
          />
          {/* Org-plane feature toggles (2-10) — registry row gates on org:flags:manage. */}
          <Route
            path="/app/settings/features"
            element={
              <GatedRoute path="/app/settings/features">
                <OrgFeaturesPage />
              </GatedRoute>
            }
          />
          {/* Org branding (story 6-8) — registry row gates on
              org:branding:manage, entitlement-masked by org.branding (Pro). */}
          <Route
            path="/app/settings/branding"
            element={
              <GatedRoute path="/app/settings/branding">
                <OrgBrandingPage />
              </GatedRoute>
            }
          />
          {/* Owner-plane console (P01 §4.1) — the page itself redirects non-owners. */}
          <Route path="/platform" element={<PlatformConsolePage />} />
          {/* /app/report-presets is canonical (nav registry; e2e specs
              2-7/2-9/2-14/2-18/2-19/2-21 all assert it — the OnboardingTour
              route-pin that once also pointed here is retired, story 2-106) —
              this was a duplicate surface, collapsed to a redirect in story 2-23. */}
          <Route path="/app/settings/presets" element={<Navigate to="/app/report-presets" replace />} />
          {/* Design-system gallery (D44) — always mounted; DesignGalleryRoute
              itself branches on the featureKey (story 2-45). */}
          <Route path="/app/design" element={<DesignGalleryRoute />} />
          {/* Legacy superadmin pages retired (story 2-8) — thin redirects keep
              old bookmarks alive and land on the superseding console: member
              management → Org Access (1C-3); platform metrics/settings →
              Platform console (1C-4, which itself bounces non-owners to /app,
              matching the old pages' behavior). */}
          <Route path="/app/admin" element={<Navigate to="/platform" replace />} />
          <Route path="/app/admin/users" element={<Navigate to="/app/settings/access" replace />} />
          <Route path="/app/admin/settings" element={<Navigate to="/platform" replace />} />
          {/* In-shell module routes (host-mounted, e.g. billing) */}
          {shellModuleRoutes}
        </Route>
      </Route>

      <Route path="*" element={<CatchAllRoute />} />
    </Routes>
  );
}
